import React, { useEffect, useRef, useState } from 'react'
import BunnyFilter from '../../fragments/filter'
import BlogPostCard from './post-card'
import { graphql } from 'gatsby'
import ContentProgressBar from '../../fragments/content-progress-bar'
import { DEFAULT_ALL_POSTS } from '~/utils/constants'
import { getQueryParam, changeQueryParam } from '../../../utils/helpers'

interface BlogProps {
  block: any
  urlParams: string
  posts: {node: BlogPost}[]
  pathname: string
  allTags: {node: BlogTag}[]
  pagePrefix: string
}

const BlogPostList: React.FC<BlogProps> = ({
  block,
  posts,
  pathname,
  allTags,
  pagePrefix
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const loadRef = useRef<HTMLDivElement | null>(null)

  const allCategories = [
    {
      name: DEFAULT_ALL_POSTS,
      label: DEFAULT_ALL_POSTS,
      to: `/${pagePrefix}/`,
    },
    ...allTags.map(({ node }) => ({
      name: node.slug,
      label: node.name,
      to: `/${pagePrefix}/tag/${node.slug}/`,
    })),
  ]

  const pageParam = Number(getQueryParam('page'))

  const [currentPage, setCurrentPage] = useState(!!pageParam ? pageParam : 1)
  const [activeTag, setActiveTag] = useState<string>(
    allCategories.find(
      (tag) => tag.name.length > 0 && pathname.includes(tag.name)
    )?.name || DEFAULT_ALL_POSTS
  )
  const [list, setList] = useState<BlogPost[]>([
    ...posts
      .map((edge) => edge.node)
      .slice(0, currentPage * block.items_per_page),
  ])
  const [postCount, setPostCount] = useState(list.length)
  const [loadMore, setLoadMore] = useState(false)
  const [hasMore, setHasMore] = useState(postCount < posts.length)

  // Handle loading more articles
  useEffect(() => {
    if (loadMore && hasMore) {
      const currentLength = list.length
      const isMore = currentLength < posts.length
      const newPage = currentPage + 1
      const nextResults = isMore
        ? posts
            .map((edge) => edge.node)
            .slice(
              currentPage * block.items_per_page,
              newPage * block.items_per_page
            )
        : []
      const newList = [...list, ...nextResults]
      setList(newList)
      setLoadMore(false)
      setPostCount(newList.length)
      setHasMore(newList.length < posts.length)
      setCurrentPage(newPage)
      changeQueryParam('page', newPage)
    }
  }, [loadMore, hasMore])

  return (
    <section className={`block ${block._template} ${block.css_class_name}`}>
      <div className="container">
        <BunnyFilter
          options={allCategories}
          activeType={activeTag}
          setActiveType={setActiveTag}
          container={containerRef}
          theme="lighter"
          isLink={true}
        />
        <div ref={containerRef} className="row gy-5">
          {list.map(
            (post) => {
              const to = `/${pagePrefix}/${post.slug}/`;
              return (
                <BlogPostCard
                  key={post.id}
                  block={post}
                  to={to}
                />
              )
            }
          )}
        </div>
      </div>
      <div ref={loadRef} className="container">
        <div className="row">
          <div className="pagination">
            <ContentProgressBar current={postCount} total={posts.length} />
            <div className="pagination__load-ref">
              {hasMore && (
                <button
                  type="button"
                  className="load-ref__more"
                  onClick={() => setLoadMore(true)}
                >
                  Load more
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export const ListFragmentQuery = graphql`
  fragment ListFragment on Block {
    title
    items_per_page
  }
`

export default BlogPostList
